<template>
  <v-dialog :model-value="isVisible" persistent width="600" @close="closeModal">
    <v-form ref="form">
      <v-card>
        <v-card-title class="d-flex flex-row">
          <h4 class="text-capitalize">
            <v-icon size="small" class="mr-2">mdi-alert-circle-outline</v-icon>
            <span>
              {{
                t('yard.dialogs.yardViewAssetVisitCreateDialog.headers.createAssetVisitTestOnly')
              }}
            </span>
          </h4>
          <v-spacer></v-spacer>
          <v-btn icon="mdi-close" variant="text" density="comfortable" @click="closeModal"></v-btn>
        </v-card-title>
        <v-card-text class="d-flex flex-column gap-4">
          <p>
            {{
              t(
                'yard.dialogs.yardViewAssetVisitCreateDialog.messages.thisToolCreatesAnAssetVisitForTestingPurposesAndThePhoneNumberMustBeSetToATestNumberTheIntendedFlowMustBeDoneThroughTheCheckInApp'
              )
            }}
          </p>

          <div class="d-flex flex-column my-4">
            <v-text-field
              color="primary"
              variant="outlined"
              :label="t('yard.dialogs.yardViewAssetVisitCreateDialog.labels.companyHint')"
              :placeholder="
                t(
                  'yard.dialogs.yardViewAssetVisitCreateDialog.placeholders.companyNameThatWillBeVisiting'
                )
              "
              :rules="
                $validator.rules.required(
                  t('yard.dialogs.yardViewAssetVisitCreateDialog.labels.companyHint')
                )
              "
              required
              v-model="companyHint" />

            <phone-number-field
              v-model="driversPhone"
              outlined
              required
              :validator="$validator"
              :placeholder="
                t(
                  'yard.dialogs.yardViewAssetVisitCreateDialog.placeholders.driversPhoneForOtherCountriesUseCountryCodeEG55'
                )
              "
              :label="t('yard.dialogs.yardViewAssetVisitCreateDialog.labels.driversPhone')" />

            <br />
            <v-select
              color="primary"
              variant="outlined"
              density="compact"
              :label="t('yard.dialogs.yardViewAssetVisitCreateDialog.labels.assetVisitType')"
              :rules="
                $validator.rules.required(
                  t('yard.dialogs.yardViewAssetVisitCreateDialog.labels.assetVisitType')
                )
              "
              required
              v-model="assetVisitType"
              :items="assetVisitTypes"
              item-title="title"
              item-value="value" />

            <section v-if="assetVisitType !== AssetVisitType.PickUp && assetContainerData">
              <br />
              <h4>{{ t('yard.dialogs.yardViewAssetVisitCreateDialog.headers.trailerData') }}</h4>
              <br />
              <div
                class="d-flex flex-row gap-4"
                v-for="(_, i) of assetContainerData || []"
                :key="i">
                <v-text-field
                  color="primary"
                  :label="t('yard.dialogs.yardViewAssetVisitCreateDialog.labels.trailerCode')"
                  v-model="assetContainerData[i].code"
                  :placeholder="
                    t(
                      'yard.dialogs.yardViewAssetVisitCreateDialog.placeholders.enterTheTrailerIdentifier'
                    )
                  "
                  required
                  :rules="
                    $validator.rules.required(
                      t('yard.dialogs.yardViewAssetVisitCreateDialog.labels.trailerCode')
                    )
                  "
                  variant="outlined"
                  class="flex-grow-1" />
                <v-select
                  color="primary"
                  :label="t('yard.dialogs.yardViewAssetVisitCreateDialog.labels.trailerType')"
                  density="compact"
                  v-model="assetContainerData[i].type"
                  :rules="
                    $validator.rules.required(
                      t('yard.dialogs.yardViewAssetVisitCreateDialog.labels.trailerType')
                    )
                  "
                  required
                  variant="outlined"
                  :placeholder="
                    t(
                      'yard.dialogs.yardViewAssetVisitCreateDialog.placeholders.selectTheTrailerType'
                    )
                  "
                  :items="assetContainerTypes"
                  item-title="title"
                  item-value="value"
                  class="flex-grow-1" />
                <v-btn
                  size=""
                  icon="mdi-delete"
                  variant="text"
                  color="error"
                  click="assetContainerData"
                  @click="assetContainerData.splice(i, 1)"></v-btn>
              </div>
              <v-btn
                variant="text"
                class="mt-4"
                @click="assetContainerData.push({ code: '', type: AssetContainerType.Trailer })">
                + {{ t('yard.dialogs.yardViewAssetVisitCreateDialog.buttons.addContainer') }}
              </v-btn>
            </section>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            variant="outlined"
            @click="closeModal"
            data-testid="yard-asset-visit-depart-dialog-cancel">
            {{ t('global.buttons.nevermind') }}
          </v-btn>
          <v-btn
            class="bg-primary"
            @click="createAssetVisit"
            data-testid="yard-asset-visit-depart-dialog-submit">
            {{ t('global.buttons.confirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
import { defineComponent, ref } from 'vue';
import { AssetContainerType, AssetVisitType } from '@nova/core';
import { isFormValid } from '@satellite/plugins/util.js';
import { useNotify } from '@/composables';
import useYard from '@/modules/yard/composables/useYard';
import { camelCase } from 'lodash';
import { useI18n } from 'vue-i18n';

export default defineComponent({
  name: 'YardViewAssetVisitCreateDialog',
  emits: ['close'],
  props: {
    warehouseId: {
      type: String,
      required: true
    },
    isVisible: {
      type: Boolean,
      required: true
    }
  },
  setup(props, { emit }) {
    const { t } = useI18n();
    const yardStore = useYard();
    const form = ref(null);

    const companyHint = ref('');
    const driversPhone = ref('');
    const assetVisitType = ref(AssetVisitType.Live);
    const assetContainerData = ref([{ code: '', type: AssetContainerType.Trailer }]);

    const closeModal = () => {
      emit('close');

      // Reset form values
      companyHint.value = '';
      driversPhone.value = '';
      assetVisitType.value = AssetVisitType.Live;
      assetContainerData.value = [{ code: '', type: AssetContainerType.Trailer }];
    };

    const notify = useNotify();

    const assetVisitTypes = [
      {
        title: t('yard.dialogs.yardViewAssetVisitCreateDialog.dropDownOptions.dropTrailer'),
        value: AssetVisitType.Drop
      },
      {
        title: t('yard.dialogs.yardViewAssetVisitCreateDialog.dropDownOptions.dropAndHook'),
        value: AssetVisitType.DropHook
      },
      { title: t('core.yard.assetVisitTypes.liveLoad'), value: AssetVisitType.Live },
      {
        title: t('yard.dialogs.yardViewAssetVisitCreateDialog.dropDownOptions.pickUp'),
        value: AssetVisitType.PickUp
      }
    ];

    const assetContainerTypes = Object.keys(AssetContainerType).map(key => ({
      title: t(`core.yard.assetContainerTypes.${camelCase(key)}`),
      value: AssetContainerType[key]
    }));

    const createAssetVisit = async () => {
      if (isFormValid(form.value) && driversPhone.value) {
        try {
          // IMPORTANT: This is just for testing purposes,
          // the actual API call will be done through the check-in app
          // and the phone number must be set to a test number

          const payload = {
            latitude: 0,
            longitude: 0,
            warehouseId: props.warehouseId,
            assetDetails: {
              phone: driversPhone.value,
              companyHint: companyHint.value,
              assetVisitType: assetVisitType.value
            },
            assetContainerDetails:
              assetContainerData?.value?.map(container => ({
                containerCode: container.code,
                containerType: container.type
              })) || null
          };

          return yardStore.fakeCheckIn(payload).then(assetVisit => {
            yardStore.trackMixpanelEvent(yardStore.MixPanelEvent.CREATE_ASSET_VISIT, {
              assetVisit,
              entryPoint: 'Manual Add Asset',
              metadata: {
                asset_container_count: assetContainerData.value.length,
                asset_visit_type: assetVisitType.value
              }
            });
          });
        } catch (error) {
          notify(
            `${t(
              'yard.dialogs.yardViewAssetVisitCreateDialog.messages.failedToCreateAssetVisit'
            )}: ${error}`,
            'error'
          );
        } finally {
          closeModal();
        }
      } else {
        notify(
          t('yard.dialogs.yardViewAssetVisitCreateDialog.messages.pleaseFillInAllRequiredFields'),
          'error'
        );
      }
    };

    return {
      t,
      driversPhone,
      companyHint,
      assetContainerData,
      assetVisitTypes,
      assetContainerTypes,
      assetVisitType,
      AssetVisitType,
      AssetContainerType,
      createAssetVisit,
      closeModal,
      form
    };
  }
});
</script>
